import { useEffect, useMemo, useState, Fragment } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
  ShieldCheck,
  Plus,
  Pencil,
  Trash2,
  Save,
  RotateCcw,
  Lock,
  Users as UsersIcon,
  X,
  AlertCircle,
  Loader2,
  Check,
  RefreshCw,
} from "lucide-react";
import Banner from "@/components/Banner";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

// Empty matrix builder
function emptyPerms(modules, actions) {
  const out = {};
  modules.forEach((m) => {
    out[m.key] = {};
    actions.forEach((a) => { out[m.key][a.key] = false; });
  });
  return out;
}

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], actions: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/roles"),
        api.get("/roles/catalog"),
      ]);
      setRoles(r.data || []);
      setCatalog(c.data || { modules: [], actions: [] });
      if (!selectedId && r.data?.length) {
        setSelectedId(r.data[0].id);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const selected = useMemo(
    () => roles.find((x) => x.id === selectedId),
    [roles, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setDraft({
        name: selected.name,
        description: selected.description || "",
        permissions: selected.permissions || emptyPerms(catalog.modules, catalog.actions),
      });
      setErr("");
      setOkMsg("");
    } else {
      setDraft(null);
    }
  }, [selected, catalog.modules, catalog.actions]);

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    return (
      draft.name !== selected.name ||
      (draft.description || "") !== (selected.description || "") ||
      JSON.stringify(draft.permissions) !== JSON.stringify(selected.permissions)
    );
  }, [selected, draft]);

  const togglePerm = (mod, act) => {
    setDraft((d) => ({
      ...d,
      permissions: {
        ...d.permissions,
        [mod]: { ...(d.permissions?.[mod] || {}), [act]: !d.permissions?.[mod]?.[act] },
      },
    }));
  };

  const toggleRow = (mod, value) => {
    setDraft((d) => ({
      ...d,
      permissions: {
        ...d.permissions,
        [mod]: Object.fromEntries(catalog.actions.map((a) => [a.key, value])),
      },
    }));
  };

  const toggleColumn = (act, value) => {
    setDraft((d) => ({
      ...d,
      permissions: Object.fromEntries(
        catalog.modules.map((m) => [
          m.key,
          { ...(d.permissions?.[m.key] || {}), [act]: value },
        ]),
      ),
    }));
  };

  const save = async () => {
    if (!selected || !draft) return;
    setSaving(true);
    setErr("");
    setOkMsg("");
    try {
      // Employee matrix is server-forced to ESS defaults — only send description (and name if allowed)
      const payload =
        selected.id === "role_employee"
          ? { description: draft.description }
          : draft;
      const r = await api.put(`/roles/${selected.id}`, payload);
      setRoles((arr) => arr.map((x) => (x.id === selected.id ? { ...x, ...r.data } : x)));
      setOkMsg(selected.id === "role_employee" ? "Saved (permissions remain locked ESS defaults)" : "Saved");
      setTimeout(() => setOkMsg(""), 2500);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetDraft = () => {
    if (!selected) return;
    setDraft({
      name: selected.name,
      description: selected.description || "",
      permissions: selected.permissions,
    });
  };

  const removeRole = async () => {
    if (!selected) return;
    if (selected.is_system) return;
    if (selected.user_count > 0) {
      if (!window.confirm(`This role is assigned to ${selected.user_count} user(s). Deleting will remove it from all of them. Continue?`)) return;
    } else {
      if (!window.confirm(`Delete custom role "${selected.name}"?`)) return;
    }
    try {
      await api.delete(`/roles/${selected.id}`);
      setRoles((arr) => arr.filter((x) => x.id !== selected.id));
      setSelectedId(roles[0]?.id);
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <AdminLayout title="Roles & Permissions" breadcrumbs={["Home", "Settings", "Roles & Permissions"]}>
      <div className="grid grid-cols-12 gap-4">
        {/* Roles list */}
        <div className="col-span-12 md:col-span-3">
          <div className="bg-white border border-slate-200 rounded shadow-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#226DB4]" /> Roles
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={loadAll}
                  title="Refresh"
                  data-testid="roles-refresh-btn"
                  className="p-1 text-slate-500 hover:bg-slate-100 rounded"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setCreateOpen(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#089949] text-white rounded hover:bg-[#067A3A]"
                  data-testid="new-role-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> New
                </button>
              </div>
            </div>
            {loading ? (
              <div className="px-4 py-8 text-center text-slate-400 text-sm">Loading…</div>
            ) : (
              <ul data-testid="roles-list" className="max-h-[70vh] overflow-y-auto">
                {roles.map((r) => (
                  <li key={r.id}>
                    <button
                      onClick={() => setSelectedId(r.id)}
                      data-testid={`role-item-${r.id}`}
                      className={`w-full text-left px-4 py-2.5 text-sm border-l-[3px] border-b border-slate-100 last:border-b-0 transition ${
                        selectedId === r.id
                          ? "border-l-[#226DB4] bg-sky-50 text-slate-900 font-medium"
                          : "border-l-transparent text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate flex items-center gap-1.5">
                          {r.is_system && <Lock className="h-3 w-3 text-slate-400" />}
                          {r.name}
                        </span>
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                          {r.user_count}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{r.description || "—"}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="col-span-12 md:col-span-9">
          {!selected || !draft ? (
            <div className="bg-white border border-slate-200 rounded shadow-sm p-10 text-center text-slate-400">
              Select a role on the left.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded shadow-sm">
              <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    {selected.is_system && <Lock className="h-3.5 w-3.5 text-slate-400" title="System role" />}
                    {selected.name}
                    {selected.is_system && (
                      <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full uppercase tracking-wide">system</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                    <UsersIcon className="h-3 w-3" /> {selected.user_count} user{selected.user_count === 1 ? "" : "s"} assigned
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!selected.is_system && (
                    <button
                      onClick={removeRole}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-rose-200 text-rose-700 rounded hover:bg-rose-50"
                      data-testid="delete-role-btn"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  )}
                  <button
                    onClick={resetDraft}
                    disabled={!dirty}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
                    data-testid="reset-draft-btn"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset
                  </button>
                  <button
                    onClick={save}
                    disabled={!dirty || saving}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-60"
                    data-testid="save-role-btn"
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                  </button>
                </div>
              </div>

              {err && (
                <div className="mx-4 mt-3 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5" /> {err}
                </div>
              )}
              {okMsg && (
                <div className="mx-4 mt-3 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded text-sm">
                  {okMsg}
                </div>
              )}

              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 border-b border-slate-100">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Role name</label>
                  <input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className={inputCls}
                    disabled={selected.is_system}
                    data-testid="role-name-input"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Description</label>
                  <input
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    className={inputCls}
                    data-testid="role-desc-input"
                  />
                </div>
              </div>

              {/* Permission matrix */}
              <div className="p-4 overflow-x-auto">
                <table className="w-full text-sm border border-slate-200 rounded">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase border-r border-slate-200">Module</th>
                      {catalog.actions.map((a) => (
                        <th key={a.key} className="px-2 py-2 text-xs font-semibold text-slate-600 uppercase text-center border-r border-slate-200 last:border-r-0" title={a.desc}>
                          <div>{a.label}</div>
                          <button
                            type="button"
                            disabled={selected.id === "role_admin" || selected.id === "role_employee"}
                            onClick={() => {
                              const allOn = catalog.modules.every((m) => draft.permissions?.[m.key]?.[a.key]);
                              toggleColumn(a.key, !allOn);
                            }}
                            className="text-[9px] text-[#226DB4] hover:underline normal-case font-normal mt-0.5 disabled:opacity-40 disabled:no-underline"
                            title={`Toggle "${a.label}" for all modules`}
                            data-testid={`toggle-col-${a.key}`}
                          >
                            toggle col
                          </button>
                        </th>
                      ))}
                      <th className="px-2 py-2 text-xs font-semibold text-slate-600 uppercase text-center">All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.modules.map((m, idx) => {
                      const row = draft.permissions?.[m.key] || {};
                      const allOn = catalog.actions.every((a) => row[a.key]);
                      const prevGroup = idx > 0 ? catalog.modules[idx - 1].group : null;
                      const showGroup = m.group && m.group !== prevGroup;
                      return (
                        <Fragment key={m.key}>
                          {showGroup && (
                            <tr className="bg-slate-100/80">
                              <td
                                colSpan={catalog.actions.length + 2}
                                className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                              >
                                {m.group}
                              </td>
                            </tr>
                          )}
                          <tr className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`perm-row-${m.key}`}>
                          <td className="px-3 py-2 border-r border-slate-100 font-medium text-slate-700">{m.label}</td>
                          {catalog.actions.map((a) => {
                            const on = !!row[a.key];
                            return (
                              <td key={a.key} className="text-center border-r border-slate-100 last:border-r-0">
                                <button
                                  onClick={() => togglePerm(m.key, a.key)}
                                  disabled={selected.id === "role_admin" || selected.id === "role_employee"}
                                  data-testid={`perm-${m.key}-${a.key}`}
                                  className={`inline-flex items-center justify-center w-7 h-7 rounded transition ${
                                    on
                                      ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
                                      : "bg-slate-50 hover:bg-slate-100 text-slate-300"
                                  } ${selected.id === "role_admin" || selected.id === "role_employee" ? "opacity-60 cursor-not-allowed" : ""}`}
                                >
                                  {on ? <Check className="h-4 w-4" /> : <X className="h-3.5 w-3.5" />}
                                </button>
                              </td>
                            );
                          })}
                          <td className="text-center">
                            <button
                              onClick={() => toggleRow(m.key, !allOn)}
                              disabled={selected.id === "role_admin" || selected.id === "role_employee"}
                              data-testid={`toggle-row-${m.key}`}
                              className={`text-[10px] px-2 py-1 rounded border ${
                                allOn
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-slate-50 text-slate-500 border-slate-200"
                              }`}
                            >
                              {allOn ? "All on" : "All off"}
                            </button>
                          </td>
                        </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {selected.id === "role_admin" && (
                  <div className="mt-3 text-[11px] text-slate-500 italic flex items-center gap-1.5">
                    <Lock className="h-3 w-3" /> Admin role always has every permission. Other settings may still be edited.
                  </div>
                )}
                {selected.id === "role_employee" && (
                  <div className="mt-3 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
                    <div className="flex items-start gap-1.5 font-semibold">
                      <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      Employee role is locked — self-service defaults only (like Zoho / Keka ESS).
                    </div>
                    <ul className="list-disc pl-5 space-y-0.5 text-amber-900/90">
                      <li>My Attendance (clock in/out + month history)</li>
                      <li>Attendance Regularization (missed punch requests)</li>
                      <li>Team Leave Calendar (who’s out in your department)</li>
                      <li>Holiday Calendar (view company holidays)</li>
                      <li>My Leaves, My Letters, My Profile (built-in)</li>
                      <li>Form 16 download + tax declaration (12BB)</li>
                      <li>Payslips: Full-time employment type only</li>
                      <li>Dashboard: upcoming birthdays in <strong>your department</strong> (with date) + who’s on leave today</li>
                      <li>Employee Documents: <strong>not</strong> on ESS — Admin / HR Admin only</li>
                    </ul>
                    <p className="text-amber-900/90">
                      Need Payroll, Employees admin, or holiday <em>management</em> (add/delete)? Create a{" "}
                      <strong>custom role</strong>, grant those modules, assign it in User Management (keep primary = Employee).
                    </p>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  {catalog.actions.map((a) => (
                    <div key={a.key} className="flex items-start gap-2 px-2 py-1.5 bg-slate-50 rounded">
                      <span className="inline-block text-[10px] font-bold uppercase text-slate-500 min-w-[58px]">{a.label}</span>
                      <span className="text-slate-600">{a.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {createOpen && (
        <NewRoleModal
          catalog={catalog}
          onClose={() => setCreateOpen(false)}
          onCreated={(r) => {
            setRoles((arr) => [...arr, r]);
            setSelectedId(r.id);
            setCreateOpen(false);
          }}
        />
      )}
    </AdminLayout>
  );
}

function NewRoleModal({ catalog, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!name.trim()) { setErr("Name is required"); return; }
    setBusy(true);
    setErr("");
    try {
      const r = await api.post("/roles", {
        name: name.trim(),
        description: desc.trim(),
        permissions: emptyPerms(catalog.modules, catalog.actions),
      });
      onCreated(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-3" data-testid="new-role-modal">
        <h3 className="text-lg font-semibold text-slate-800">New Custom Role</h3>
        <div>
          <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} autoFocus data-testid="new-role-name" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Description</label>
          <input value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} data-testid="new-role-desc" />
        </div>
        <div className="text-[11px] text-slate-500 italic">
          You&apos;ll set the permission matrix on the next screen after the role is created.
        </div>
        {err && <Banner variant="error">{err}</Banner>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-200 rounded">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium" data-testid="new-role-submit">
            {busy ? "Creating…" : "Create role"}
          </button>
        </div>
      </div>
    </div>
  );
}
